import { useState } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { cn } from '@/shared/lib/utils';
import { useNotificationsStore } from '../store';
import { useNotificationStream } from '../hooks/useNotificationStream';
import { useTabTitleCounter } from '../hooks/useTabTitleCounter';
import { usePrintOnNewOrder } from '@/features/printer/hooks/usePrintOnNewOrder';
import { NotificationCenter } from './NotificationCenter';

interface Props {
  restaurantId: string;
}

/**
 * Bell + popover. Mounted once per restaurant-scoped page; owns the SSE
 * connection via `useNotificationStream` and the tab-title counter.
 * Autoplay-policy handling lives in utils/sound — no per-gesture re-prime
 * needed here; the sound module queues and plays on next user interaction.
 */
export function NotificationBell({ restaurantId }: Props) {
  const [open, setOpen] = useState(false);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);

  useNotificationStream(restaurantId);
  useTabTitleCounter();
  // Electron-only: auto-print every new order onto the configured printer.
  // No-op outside the desktop app and when the printer toggle is off.
  usePrintOnNewOrder(restaurantId);

  const display = unreadCount >= 100 ? '99+' : unreadCount.toString();
  const ariaLabel = unreadCount > 0
    ? `Notifications, ${unreadCount} unread`
    : 'Notifications';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 sm:h-10 sm:w-10"
          aria-label={ariaLabel}
        >
          <Bell className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
          {unreadCount > 0 && (
            <span
              className={cn(
                'absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1',
                'rounded-full bg-destructive text-destructive-foreground',
                'text-[10px] font-semibold flex items-center justify-center',
                'ring-2 ring-background'
              )}
            >
              {display}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="p-0 w-auto" collisionPadding={8}>
        <NotificationCenter restaurantId={restaurantId} onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
